import React, { useEffect, useRef } from 'react';
import { Box, List, ListItem, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material';

import type { ExecutionEvent } from '../../hooks/useExecutionStatus';

type Props = {
  events: ExecutionEvent[];
};

const EVENT_COLORS: Record<ExecutionEvent['type'], string> = {
  info: 'text.primary',
  warning: 'warning.main',
  error: 'error.main',
  success: 'success.main'
};

const formatTime = (isoString: string) => {
  const date = new Date(isoString);
  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3
  });
};

const EventLogTab: React.FC<Props> = ({ events }) => {
  const theme = useTheme();
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [events.length]);

  if (events.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No events yet
        </Typography>
      </Box>
    );
  }

  return (
    <List
      dense
      sx={{
        overflow: 'auto',
        height: '100%',
        py: 0,
        fontFamily: 'monospace',
        fontSize: '0.8rem'
      }}
    >
      {events.map((event, i) => (
        <ListItem
          key={`${event.timestamp}-${i}`}
          sx={{
            py: 0.25,
            px: 2,
            display: 'flex',
            alignItems: 'flex-start',
            '&:hover': {
              backgroundColor: theme.palette.action.hover
            }
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontFamily: 'monospace',
              color: 'text.secondary',
              mr: 1.5,
              flexShrink: 0
            }}
          >
            {formatTime(event.timestamp)}
          </Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              component="div"
              sx={{
                fontFamily: 'monospace',
                color: EVENT_COLORS[event.type]
              }}
            >
              {event.message}
            </Typography>
            {event.description && (
              <Tooltip title={event.description} placement="bottom-start" arrow>
                <Typography
                  variant="caption"
                  component="div"
                  sx={{
                    color: 'text.secondary',
                    fontStyle: 'italic',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    whiteSpace: 'normal',
                    lineHeight: 1.3
                  }}
                >
                  {event.description}
                </Typography>
              </Tooltip>
            )}
          </Box>
        </ListItem>
      ))}
      <div ref={bottomRef} />
    </List>
  );
};

export default EventLogTab;
